/**
 * src/components/student-dashboard-components/theme/components/InfoCard.js
 *
 * React Component Module.
 *
 * Purpose: React UI module containing one or more components and local UI helpers.
 *
 * Data flow (high level):
 * - UI/Caller -> this module -> downstream helpers/services -> state/response.
 */
import React, { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../ThemeContext';
import { getColor, createTransition } from '../themeUtils';
import Card from './Card';
import Button from './Button';
import Input from './Input';
import ToggleSwitch from './ToggleSwitch';
import PremiumPhoneInput from '../../../shared/PremiumPhoneInput';

const InfoCardHeader = styled.div`
  padding: ${props => props.theme.spacing.lg};
  background: ${props => props.$bgColor || getColor('primary.blue')};
  color: ${props => props.$textColor || getColor('neutral.white')};
  border-radius: ${props => props.theme.borderRadius.lg} ${props => props.theme.borderRadius.lg} 0 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  ${props => props.theme.isMobile && `
    padding: ${props.theme.spacing.md};
    border-radius: ${props.theme.borderRadius.md} ${props.theme.borderRadius.md} 0 0;
  `}
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const IconContainer = styled.div`
  padding: ${props => props.theme.spacing.sm};
  background: ${getColor('neutral.white')};
  border-radius: ${props => props.theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${props => props.theme.shadows.sm};
`;

const HeaderTitle = styled.h3`
  margin: 0;
  font-family: ${props => props.theme.typography.fontFamily.primary};
  font-size: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.h4.fontSize : 
    props.theme.typography.desktop.h4.fontSize};
  font-weight: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.h4.fontWeight : 
    props.theme.typography.desktop.h4.fontWeight};
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const ActionButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: ${props => props.theme.borderRadius.sm};
  color: ${getColor('neutral.white')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.xs};
  ${createTransition('background', 'fast')};
  min-height: 40px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const InfoCardContent = styled.div`
  padding: ${props => props.theme.spacing.lg};
  
  ${props => props.theme.isMobile && `
    padding: ${props.theme.spacing.md};
  `}
`;

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  min-width: 0;

  ${props => props.$toggle && `
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${props.theme.spacing.md};
  `}
  
  ${props => !props.theme.isMobile && !props.$stack && !props.$editing && `
    display: grid;
    grid-template-columns: minmax(8.5rem, 12rem) minmax(0, 1fr);
    align-items: start;
    column-gap: ${props.theme.spacing.lg};
  `}

  ${props => !props.theme.isMobile && props.$toggle && `
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${props.theme.spacing.lg};
  `}
`;

const FieldLabel = styled.label`
  font-family: ${props => props.theme.typography.fontFamily.primary};
  font-size: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.small.fontSize : 
    props.theme.typography.desktop.small.fontSize};
  font-weight: 500;
  color: ${getColor('neutral.dark')};
  line-height: 1.4;
  min-width: 0;

  ${props => props.$toggle && `
    flex: 1;
    margin: 0;
  `}
`;

const FieldValue = styled.div`
  font-family: ${props => props.theme.typography.fontFamily.primary};
  font-size: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.body.fontSize : 
    props.theme.typography.desktop.body.fontSize};
  font-weight: 400;
  color: ${getColor('neutral.black')};
  min-height: 1.5rem;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  width: auto;
  min-width: 0;
  text-align: left;
  word-break: break-word;
  overflow-wrap: anywhere;
  
  ${props => props.$editing && `
    align-items: stretch;
    width: 100%;
  `}

  ${props => props.$toggle && `
    min-height: auto;
    width: auto;
    flex-shrink: 0;
    align-items: center;
    justify-content: flex-end;
  `}
`;

const ErrorMessage = styled.div`
  color: ${getColor('semantic.error')};
  font-size: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.caption.fontSize : 
    props.theme.typography.desktop.caption.fontSize};
  margin-top: ${props => props.theme.spacing.xs};
`;

const DropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  margin-top: 0;
  width: 100%;
`;

const DropdownOption = styled.button`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${getColor('neutral.medium')};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.$active ? props.$bgColor : getColor('neutral.white')};
  color: ${props => props.$active ? props.$textColor : getColor('neutral.black')};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  ${createTransition('all', 'fast')};
  
  &:hover {
    background: ${props => props.$active ? props.$bgColor : getColor('neutral.light')};
  }
`;

const EditActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.lg};
  padding-top: ${props => props.theme.spacing.md};
  border-top: 1px solid ${getColor('neutral.medium')};
  
  ${props => props.theme.isMobile && `
    flex-direction: column-reverse;
  `}
`;

const FooterButton = styled(Button)`
  min-width: 120px;
  
  ${props => props.theme.isMobile && `
    width: 100%;
  `}
`;

/**
 * Info Card.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const InfoCard = React.forwardRef(({
  icon: Icon,
  title,
  bgColor,
  textColor,
  iconColor,
  fields = [],
  children,
  onSave,
  showEdit = true,
  autoEdit = false,
  editText,
  saveText,
  cancelText,
  savingText,
  className,
  ...props
}, ref) => {
  const { t, i18n } = useTranslation('global');
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [editedFields, setEditedFields] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const resolvedEditText = editText || t('common.edit', { defaultValue: 'Edit' });
  const resolvedSaveText = saveText || t('saveChanges', { defaultValue: 'Save changes' });
  const resolvedCancelText = cancelText || t('common.cancel', { defaultValue: 'Cancel' });
  const resolvedSavingText = savingText || t('common.Saving', { defaultValue: 'Saving...' });
  
    /**
   * Handle Start Edit.
   *
   * React component or UI helper used to render and manage local UI interactions.
   */
   const handleStartEdit = useCallback(() => {
    const initialEdits = {};
    fields.forEach(field => {
      if (field.editable !== false && !field.readOnly) {
        initialEdits[field.label] = field.value || '';
      }
    });
    setEditedFields(initialEdits);
    setIsEditing(true);
    setError(null);
  }, [fields]);

  useEffect(() => {
    if (autoEdit && !isEditing) {
      handleStartEdit();
    }
  }, [autoEdit, isEditing, handleStartEdit]);
  
    /**
   * Handle Save.
   *
   * React component or UI helper used to render and manage local UI interactions.
   */
   const handleSave = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await onSave?.(editedFields);
      setIsEditing(false);
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };
  
    /**
   * Handle Cancel.
   *
   * React component or UI helper used to render and manage local UI interactions.
   */
   const handleCancel = () => {
    setIsEditing(false);
    setEditedFields({});
    setError(null);
  };
  
    /**
   * Render Field Value.
   *
   * React component or UI helper used to render and manage local UI interactions.
   */
   const renderFieldValue = (field) => {
    if (field.value == null) return '';

    if (typeof field.value === 'object' && !Array.isArray(field.value)) {
      if ('street' in field.value) return field.value.street;
      if ('city' in field.value) return field.value.city;
      if ('postalCode' in field.value) return field.value.postalCode;
      if ('country' in field.value) return field.value.country;
    }

    // For select fields with {value, label} options, show label instead of raw value
    if (field.type === 'select' && field.options) {
      const opt = field.options.find((o) =>
        typeof o === 'string' ? o === field.value : o.value === field.value
      );
      if (opt && typeof opt !== 'string') return opt.label;
    }

    const text = field.value.toString();
    if (text.includes('\n')) {
      return <span style={{ whiteSpace: 'pre-line' }}>{text}</span>;
    }
    return text;
  };
  
    /**
   * Render Field Input.
   *
   * React component or UI helper used to render and manage local UI interactions.
   */
   const renderFieldInput = (field) => {
    if (field.type === 'toggle') {
      return (
        <ToggleSwitch
          checked={editedFields[field.label] || false}
          onChange={(checked) => setEditedFields({
            ...editedFields,
            [field.label]: checked
          })}
        />
      );
    }
    
    if (field.type === 'select' && field.options) {
      const normalizedOptions = field.options.map((opt) =>
        typeof opt === 'string' ? { value: opt, label: opt } : opt
      );
      return (
        <select
          value={editedFields[field.label] || ''}
          onChange={(e) => setEditedFields({
            ...editedFields,
            [field.label]: e.target.value
          })}
          style={{
            width: '100%',
            padding: '0.75rem 1rem',
            border: `2px solid ${getColor('neutral.medium')}`,
            borderRadius: theme.borderRadius.md,
            minHeight: theme.isMobile ? '44px' : '42px',
            background: getColor('neutral.white'),
            color: getColor('neutral.black'),
            fontFamily: theme.typography.fontFamily.primary,
            fontSize: theme.isMobile ?
              theme.typography.mobile.body.fontSize :
              theme.typography.desktop.body.fontSize
          }}
        >
          {normalizedOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }
    
    if (field.type === 'tel') {
      return (
        <PremiumPhoneInput
          value={editedFields[field.label] || ''}
          onChange={(nextValue) => setEditedFields({
            ...editedFields,
            [field.label]: nextValue
          })}
          placeholder={field.placeholder}
          country={field.country || 'PL'}
          locale={i18n.language}
          inputProps={{
            name: field.name,
            required: field.required,
            autoComplete: field.autoComplete || 'tel',
            ...(field.inputProps || {})
          }}
        />
      );
    }

    return (
      <Input
        type={field.type || 'text'}
        value={editedFields[field.label] || ''}
        onChange={(e) => setEditedFields({
          ...editedFields,
          [field.label]: e.target.value
        })}
        placeholder={field.placeholder}
      />
    );
  };
  
  return (
    <Card ref={ref} variant="elevated" padding="none" className={className} {...props}>
      <InfoCardHeader theme={theme} $bgColor={bgColor} $textColor={textColor}>
        <HeaderLeft theme={theme}>
          {Icon && (
            <IconContainer theme={theme}>
              <Icon 
                size={theme.isMobile ? 16 : 20} 
                color={iconColor || getColor('primary.blue')} 
              />
            </IconContainer>
          )}
          <HeaderTitle theme={theme}>{title}</HeaderTitle>
        </HeaderLeft>
        
        {showEdit && !isEditing && fields.length > 0 && (
          <ActionButtons theme={theme}>
            <ActionButton
              theme={theme}
              onClick={handleStartEdit}
              disabled={isLoading}
              aria-label={resolvedEditText}
              title={resolvedEditText}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
              </svg>
              {!theme.isMobile && <span>{resolvedEditText}</span>}
            </ActionButton>
          </ActionButtons>
        )}
      </InfoCardHeader>
      
      <InfoCardContent theme={theme}>
        <FieldContainer theme={theme}>
          {fields.map((field, index) => {
            if (field.visibleWhen && !field.visibleWhen(isEditing ? editedFields : null)) return null;
            const isToggleField = field.type === 'toggle';
            return (
            <FieldRow
              key={index}
              theme={theme}
              $editing={Boolean(isEditing && field.editable !== false && !field.readOnly)}
              $stack={Boolean(field.dropdown && !isEditing)}
              $toggle={isToggleField}
            >
              <FieldLabel theme={theme} $toggle={isToggleField}>{field.label}</FieldLabel>
              <FieldValue
                theme={theme}
                $editing={Boolean(isEditing && field.editable !== false && !field.readOnly)}
                $toggle={isToggleField}
              >
                {isEditing && field.editable !== false && !field.readOnly ? (
                  renderFieldInput(field)
                ) : field.type === 'toggle' ? (
                  <ToggleSwitch
                    checked={field.value}
                    onChange={field.onChange || (() => {})}
                    disabled={!field.onChange}
                  />
                ) : field.dropdown && !isEditing ? (
                  <DropdownContainer theme={theme}>
                    {field.dropdown.options.map((option) => (
                      <DropdownOption
                        key={option.code}
                        theme={theme}
                        $active={option.active}
                        $bgColor={bgColor}
                        $textColor={textColor}
                        onClick={() => field.dropdown.onChange(option.code, option.name)}
                      >
                        {option.flag && (
                          <img
                            src={option.flag}
                            alt=""
                            aria-hidden="true"
                            role="presentation"
                            style={{ width: '20px', height: '15px', objectFit: 'cover' }}
                          />
                        )}
                        <span>{option.name}</span>
                        {option.active && (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: 'auto' }}>
                            <polyline points="20,6 9,17 4,12"></polyline>
                          </svg>
                        )}
                      </DropdownOption>
                    ))}
                  </DropdownContainer>
                ) : (
                  renderFieldValue(field)
                )}
              </FieldValue>
            </FieldRow>
            );
          })}
        </FieldContainer>

        {showEdit && isEditing && (
          <EditActions theme={theme}>
            <FooterButton
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
              aria-label={resolvedCancelText}
            >
              {resolvedCancelText}
            </FooterButton>
            <FooterButton
              variant="primary"
              onClick={handleSave}
              loading={isLoading}
              aria-label={isLoading ? resolvedSavingText : resolvedSaveText}
            >
              {resolvedSaveText}
            </FooterButton>
          </EditActions>
        )}
        
        {error && (
          <ErrorMessage theme={theme}>
            {error}
          </ErrorMessage>
        )}
        
        {children}
      </InfoCardContent>
    </Card>
  );
});

export default InfoCard;
